"use client";

import { ReactNode } from "react";

import {
  AbstractIntlMessages,
  IntlErrorCode,
  NextIntlClientProvider,
} from "next-intl";

// Client-side deduplication cache to prevent console spam
const clientWarnedKeys = new Set<string>();
const clientKeyUsageCount = new Map<string, number>();

/**
 * Client-side deduplication for missing translation warnings
 * Prevents console spam when same key is missing multiple times
 */
const warnOncePerKeyClient = (key: string, message: string): void => {
  // Always count usage for statistics
  clientKeyUsageCount.set(key, (clientKeyUsageCount.get(key) || 0) + 1);

  // Only log warning once per key
  if (!clientWarnedKeys.has(key)) {
    clientWarnedKeys.add(key);
    const usageCount = clientKeyUsageCount.get(key) || 1;
    console.warn(
      message + (usageCount > 1 ? ` (used ${usageCount} times)` : ""),
    );
  }
};

// Safe wrapper that only logs to console - no external modules needed
const reportMissingTranslation = (namespace: string, key: string) => {
  // Skip in production or when debugging disabled
  if (process.env.NODE_ENV === "production") {
    return;
  }

  if (
    process.env.NEXT_PUBLIC_DEBUG_TRANSLATIONS !== "1" &&
    process.env.NEXT_DEBUG_TRANSLATIONS !== "1"
  ) {
    return;
  }

  // Use deduplication to prevent console spam
  warnOncePerKeyClient(
    `${namespace}.${key}`,
    `[i18n-debug] Missing translation: ${namespace}.${key}`,
  );
};

/**
 * Show client-side missing translation statistics
 * Can be called from browser console for debugging
 */
export const showClientTranslationStats = (): void => {
  if (clientKeyUsageCount.size === 0) {
    console.log("[i18n-client] No missing translations recorded");
    return;
  }

  const sortedKeys = Array.from(clientKeyUsageCount.entries()).sort(
    ([, a], [, b]) => b - a,
  );

  console.warn(
    `[i18n-client] Missing translation summary (${sortedKeys.length} keys):`,
  );
  sortedKeys.forEach(([key, count]) => {
    console.warn(`  "${key}" - used ${count} times`);
  });
};

/**
 * Reset client-side warning cache
 * Useful for testing or when navigating
 */
export const resetClientTranslationCache = (): void => {
  clientWarnedKeys.clear();
  clientKeyUsageCount.clear();
  console.log("[i18n-client] Warning cache reset");
};

// Make functions available globally for debugging
if (typeof window !== "undefined") {
  (window as any).showClientTranslationStats = showClientTranslationStats;
  (window as any).resetClientTranslationCache = resetClientTranslationCache;
}

interface II18nProviderProps {
  children: ReactNode;
  messages: AbstractIntlMessages;
  locale: string;
}

export function I18nProvider({
  children,
  messages,
  locale,
}: II18nProviderProps) {
  return (
    <NextIntlClientProvider
      messages={messages}
      locale={locale}
      onError={(err) => {
        if (err.code === IntlErrorCode.MISSING_MESSAGE) {
          const match = err.message.match(/`(.+?)`/); // extract `namespace.key`
          const fullPath = match ? match[1] : undefined;
          if (fullPath) {
            const [namespace, ...keyParts] = fullPath.split(".");
            const key = keyParts.join(".");
            reportMissingTranslation(namespace, key);
            // Use deduplication for the main warning too
            warnOncePerKeyClient(
              fullPath,
              `[i18n] Missing translation: ${fullPath}`,
            );
          } else {
            warnOncePerKeyClient(
              err.message || "unknown",
              `[i18n] Missing translation: ${err.message}`,
            );
          }
        } else {
          console.error(`[i18n] Intl error: ${err.message}`);
        }
      }}
    >
      {children}
    </NextIntlClientProvider>
  );
}
